// Piezas chicas que comparten todos los easter eggs: el contador de secretos, el aviso
// breve, la apertura de cada secreto y Midnight City. Todo vive en el navegador del
// visitante (02-EASTER-EGGS.md, 1.6): nada se manda al servidor.

import { countedSecrets, type SecretId } from "@/content/easter";
import { worldBus } from "@/lib/world-bus";

const TOAST_EVENT = "yvyrai:toast";
const SECRETS_KEY = "yvyrai:secretos";
const ALL_SHOWN_KEY = "yvyrai:secretos-todos";
const MIDNIGHT_KEY = "yvyrai:midnight";

export type ToastDetail = { text: string; sub?: string };

function storageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string | null) {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Navegación privada o almacenamiento lleno: el secreto funciona igual, solo no se recuerda.
  }
}

// ---- Secretos ----

export function foundSecrets(): SecretId[] {
  try {
    const list = JSON.parse(storageGet(SECRETS_KEY) ?? "[]") as unknown;
    return Array.isArray(list) ? (list.filter((s) => countedSecrets.includes(s as SecretId)) as SecretId[]) : [];
  } catch {
    return [];
  }
}

export function markSecret(id: SecretId) {
  const list = foundSecrets();
  if (list.includes(id)) return;
  list.push(id);
  storageSet(SECRETS_KEY, JSON.stringify(list));
}

export function secretsProgress() {
  return { found: foundSecrets().length, total: countedSecrets.length };
}

/** Encontró todos y todavía no vio la línea especial (se muestra una sola vez, 7). */
export function allFoundPending(): boolean {
  const { found, total } = secretsProgress();
  return found >= total && !storageGet(ALL_SHOWN_KEY);
}

export function markAllFoundShown() {
  storageSet(ALL_SHOWN_KEY, "1");
}

// ---- Avisos breves (un evento del documento: cualquier secreto puede pedir uno) ----

export function toast(text: string, sub?: string) {
  window.dispatchEvent(new CustomEvent<ToastDetail>(TOAST_EVENT, { detail: { text, sub } }));
}

export function onToast(fn: (d: ToastDetail) => void) {
  const h = (e: Event) => fn((e as CustomEvent<ToastDetail>).detail);
  window.addEventListener(TOAST_EVENT, h);
  return () => window.removeEventListener(TOAST_EVENT, h);
}

// ---- Utilidades ----

/** Foco en un campo de texto: ahí no se dispara ningún secreto por teclado. */
export function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

export function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// ---- Midnight City (3.4) ----

export function isMidnight(): boolean {
  return document.documentElement.dataset.theme === "midnight";
}

/** Cambia la paleta, la recuerda en el navegador y avisa a la escena. */
export function setMidnight(on: boolean) {
  const root = document.documentElement;
  if (on) root.dataset.theme = "midnight";
  else delete root.dataset.theme;
  worldBus.midnight = on;
  storageSet(MIDNIGHT_KEY, on ? "1" : null);
  if (on) markSecret("midnight");
}

/** Para el script de arranque de layout.tsx: misma clave, antes del primer pintado. */
export const MIDNIGHT_STORAGE_KEY = MIDNIGHT_KEY;
